import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

const VIEWPORT_MARGIN = 8

interface AnchoredPopoverOptions {
  align?: 'left' | 'right'
  width: number
  maxHeight: number
}

export function useAnchoredPopover({
  align = 'left',
  width,
  maxHeight,
}: AnchoredPopoverOptions) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const openUpward = spaceBelow < maxHeight + VIEWPORT_MARGIN && rect.top > spaceBelow
    const preferredLeft = align === 'left' ? rect.left : rect.right - width
    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN
    setPosition({
      top: openUpward ? Math.max(VIEWPORT_MARGIN, rect.top - maxHeight - 8) : rect.bottom + 8,
      left: Math.max(VIEWPORT_MARGIN, Math.min(preferredLeft, maxLeft)),
    })
  }, [align, maxHeight, width])

  useLayoutEffect(() => {
    if (open) updatePosition()
  }, [open, updatePosition])

  useEffect(() => {
    if (!open) {
      setPosition(null)
      return
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (!triggerRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
        setOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    const onReposition = () => updatePosition()
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', onReposition)
    window.addEventListener('scroll', onReposition, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', onReposition)
      window.removeEventListener('scroll', onReposition, true)
    }
  }, [open, updatePosition])

  return { open, setOpen, position, triggerRef, popoverRef, width, maxHeight }
}
